import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/api/client";
import { useBackends } from "@/api/backends";
import { useStartRun } from "@/api/runs";
import { useTemplates } from "@/api/templates";
import type { RunTemplateBinding } from "@/api/types";

/** One backend kind's verdict, as computed by runner/compatibility.py. */
export interface BackendCompatibility {
  /** "ok" | "blocked" | "mismatch" */
  status: string;
  code: string | null;
  reason: string;
  runnable: boolean;
}

/** Minimal scenario shape required by this form. */
export interface ScenarioForRun {
  id: string;
  streaming?: {
    supported_backends?: string[];
  };
  /** Ordered list of backend mappings; determines checkbox order. */
  backends?: {
    backend: string;
    services?: unknown[];
  }[];
  /** Which cloud the incident is written against: "aws" | "azure" | null. */
  cloud?: string | null;
  /** Per-backend-kind verdict, keyed by kind. */
  compatibility?: Record<string, BackendCompatibility>;
}

interface Props {
  scenario: ScenarioForRun;
  canMutate?: boolean;
  mutationReason?: string | null;
}

export function ScenarioRunForm({ scenario, canMutate = true, mutationReason = null }: Props) {
  const navigate = useNavigate();
  const backends = useBackends();
  const templates = useTemplates();
  const startRun = useStartRun();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bindings, setBindings] = useState<RunTemplateBinding[]>([]);
  const [error, setError] = useState<string | null>(null);

  function toggleBinding(templateId: string) {
    setBindings((prev) =>
      prev.some((b) => b.template_id === templateId)
        ? prev.filter((b) => b.template_id !== templateId)
        : [...prev, { template_id: templateId, rate_per_minute: null }],
    );
  }

  function setRate(templateId: string, value: string) {
    const rate = value ? Number(value) : null;
    setBindings((prev) =>
      prev.map((b) => (b.template_id === templateId ? { ...b, rate_per_minute: rate } : b)),
    );
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canMutate || selectedIds.length === 0) return;
    setError(null);
    // Submit backend_ids in the scenario's declared backends order (via
    // `orderedBackends`) rather than the user's checkbox click order.
    // The engine routes refire + noise Raven credentials from backends[0];
    // if we submitted in click order, ticking aws then datadog on a
    // scenario declared `backends: [datadog, aws]` would route alerts to
    // aws's Raven project instead of datadog's — the opposite of the
    // scenario author's intent.
    const selectedSet = new Set(selectedIds);
    const orderedIds = orderedBackends.map((b) => b.id).filter((id) => selectedSet.has(id));
    startRun.mutate(
      {
        scenario_id: scenario.id,
        backend_ids: orderedIds,
        template_bindings: bindings,
      },
      {
        onSuccess: (run) => navigate(`/runs/${run.id}`),
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : String(err)),
      },
    );
  }

  /**
   * Every registered backend is listed, with incompatible ones shown disabled
   * and labelled with the reason rather than silently omitted — a backend that
   * quietly isn't there reads as a missing backend, not as a scenario that
   * cannot run on it.
   *
   * The verdict comes from the server (runner/compatibility.py) so the picker
   * and the run-start gate cannot disagree. `supported_backends` is only a
   * fallback for an older payload that predates the compatibility field.
   */
  const compat = scenario.compatibility ?? {};
  const fallbackKinds = (scenario.streaming?.supported_backends ?? []).map((s) =>
    s.toLowerCase(),
  );

  function verdictFor(kind: string): BackendCompatibility {
    const v = compat[kind.toLowerCase()];
    if (v) return v;
    return fallbackKinds.length === 0 || fallbackKinds.includes(kind.toLowerCase())
      ? { status: "ok", code: null, reason: "", runnable: true }
      : {
          status: "blocked",
          code: "backend_kind_unsupported",
          reason: `scenario does not support ${kind} backends`,
          runnable: false,
        };
  }

  const allBackends = backends.data ?? [];
  const eligible = allBackends;

  /**
   * Order by the scenario's backends[] array (which reflects the YAML order),
   * falling back to name-sort for any eligible backend not listed there.
   */
  const scenarioOrder = (scenario.backends ?? []).map((b) => b.backend);
  const orderedBackends = [...eligible].sort((a, b) => {
    const ai = scenarioOrder.indexOf(a.kind);
    const bi = scenarioOrder.indexOf(b.kind);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.name.localeCompare(b.name);
  });

  const runnableBackends = orderedBackends.filter((b) => verdictFor(b.kind).runnable);
  const noBackendsAtAll = !backends.isLoading && orderedBackends.length === 0;
  const noRunnableBackends =
    !backends.isLoading && orderedBackends.length > 0 && runnableBackends.length === 0;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <fieldset>
          <legend className="text-sm font-medium">Backends</legend>
          {backends.isLoading && (
            <p className="text-xs text-muted-foreground mt-1">Loading backends…</p>
          )}
          {noBackendsAtAll && (
            <p className="text-xs text-muted-foreground mt-1">
              No backends configured. Add one on the Backends tab.
            </p>
          )}
          {noRunnableBackends && (
            <p className="text-xs text-muted-foreground mt-1">
              None of the configured backends can run this scenario
              {scenario.cloud ? ` — it is written against ${scenario.cloud} resources` : ""}.
              Reasons are shown against each backend below.
            </p>
          )}
          <div className="mt-1 space-y-1">
            {orderedBackends.map((b) => {
              const v = verdictFor(b.kind);
              const blocked = !v.runnable;
              return (
                <div key={b.id}>
                  <label
                    className={
                      "flex items-center gap-2 text-sm " +
                      (blocked || !canMutate ? "cursor-not-allowed opacity-60" : "cursor-pointer")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(b.id)}
                      disabled={!canMutate || blocked}
                      onChange={(e) => {
                        setSelectedIds((prev) =>
                          e.target.checked ? [...prev, b.id] : prev.filter((id) => id !== b.id),
                        );
                      }}
                      className="accent-primary"
                    />
                    <span>
                      {b.name}{" "}
                      <span className="text-muted-foreground text-xs">({b.kind})</span>
                      {v.status === "mismatch" && (
                        <span
                          className="ml-2 rounded border border-amber-500/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-500"
                          title={v.reason}
                        >
                          wrong cloud
                        </span>
                      )}
                      {v.status === "blocked" && (
                        <span
                          className="ml-2 rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                          title={v.reason}
                        >
                          unsupported
                        </span>
                      )}
                    </span>
                  </label>
                  {blocked && v.reason && (
                    <p className="ml-6 text-xs text-muted-foreground">{v.reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
        {selectedIds.length === 0 && runnableBackends.length > 0 && (
          <p className="text-xs text-muted-foreground">Select at least one backend to start.</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Noise templates</Label>
        <div className="border border-border rounded-md p-2 space-y-2 max-h-48 overflow-auto">
          {(templates.data ?? []).map((t) => {
            const binding = bindings.find((b) => b.template_id === t.id);
            return (
              <div key={t.id} className="flex items-center gap-2">
                <input
                  id={`tpl-${t.id}`}
                  type="checkbox"
                  checked={!!binding}
                  disabled={!canMutate}
                  onChange={() => toggleBinding(t.id)}
                  className="accent-primary"
                />
                <label
                  htmlFor={`tpl-${t.id}`}
                  className="flex-1 text-sm text-foreground cursor-pointer"
                >
                  {t.name}
                </label>
                {binding && (
                  <Input
                    className="w-20 h-8"
                    type="number"
                    min={1}
                    disabled={!canMutate}
                    placeholder={`${t.default_rate_per_minute}`}
                    onChange={(e) => setRate(t.id, e.target.value)}
                  />
                )}
              </div>
            );
          })}
          {(templates.data ?? []).length === 0 && (
            <p className="text-xs text-muted-foreground px-1">No noise templates yet.</p>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!canMutate || selectedIds.length === 0 || startRun.isPending}
          title={!canMutate ? (mutationReason ?? "Insufficient permission") : undefined}
        >
          {startRun.isPending ? "Starting…" : "Start run"}
        </Button>
      </div>
      {!canMutate && (
        <p className="text-xs text-muted-foreground text-right">
          {mutationReason ?? "Insufficient permissions for this action."}
        </p>
      )}
    </form>
  );
}
